import {
    type TextStyle,
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    type BadgeInverseColorsConstantType,
    type BadgeColorVariantConstantType,
    type BadgeIconColorKeyConstantType,
    type BadgeCustomColorConstantType,
    type BadgeColorRolesConstantType,
    type BadgeHitSlopConstantType,
    type BadgeColorsConstantType,
    type BadgeVariantColorRoles,
    type BadgeDynamicStyleType,
    type BadgeSizeConstantType,
    type BadgeInverseColors,
    type BadgeMeasures,
    type BadgeColors
} from "./type";
import {
    type TypeColorRolesType,
    type TypeColorVariant,
    getColorRoleValue,
    TYPE_COLOR_ROLES,
    getTypeColors
} from "../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../types";
import {
    getControlMeasures,
    getHitArea
} from "../../helpers/theme/control";
import {
    getFocusRingStyle
} from "../../helpers/theme/state";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getHugStyle
} from "../../helpers/theme/spread";
import {
    webStyle
} from "../../utils";

export const BADGE_VARIANT_COLOR_ROLES: BadgeVariantColorRoles = {
    outline: "outline",
    solid: "filled",
    tonal: "tonal",
    ghost: "ghost"
};

export const getBadgeColorRoles = ({
    variant,
    type
}: BadgeColorRolesConstantType): TypeColorRolesType => {
    return TYPE_COLOR_ROLES[type][BADGE_VARIANT_COLOR_ROLES[variant]];
};

export const getBadgeColorVariant = ({
    isDisabled,
    variant
}: BadgeColorVariantConstantType): TypeColorVariant => {
    if(!isDisabled) {
        return BADGE_VARIANT_COLOR_ROLES[variant];
    }

    return variant === "ghost" ? "ghost" : "tonal";
};

export const getBadgeSize = ({
    controlPaddings,
    controlHeights,
    shapeConfigs,
    typography,
    iconSizes,
    borders,
    spaces,
    size
}: BadgeSizeConstantType): BadgeMeasures => {
    const currentSize = getControlMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        step: -1,
        size
    });

    if(size !== "small") {
        return currentSize;
    }

    return {
        ...currentSize,
        minHeight: typography[currentSize.labelVariant].lineHeight + Math.max(spaces.spacingXs, borders.line * 2)
    };
};

export const getBadgeHitSlop = ({
    touchTargets,
    currentSize,
    pointerType
}: BadgeHitSlopConstantType): number => {
    const hitArea = getHitArea({
        visualSize: currentSize.minHeight,
        touchTargets,
        pointerType
    });

    return Math.max(0, (hitArea.minHeight - currentSize.minHeight) / 2);
};

export const getBadgeIconColorKey = ({
    isDisabled,
    variant,
    type
}: BadgeIconColorKeyConstantType): keyof NCoreUIKit.IconContentColors => {
    if(isDisabled) {
        return "disabled";
    }

    const currentRole = getBadgeColorRoles({
        variant,
        type
    }).iconColor;

    if(currentRole.group === "content.icon") {
        return currentRole.key;
    }

    const outlineRole = TYPE_COLOR_ROLES[type].outline.iconColor;

    return outlineRole.group === "content.icon" ? outlineRole.key : "default";
};

export const getBadgeCustomColor = ({
    tokenColors = {},
    customColor,
    colors
}: BadgeCustomColorConstantType): string => {
    const _customColor = customColor as keyof NCoreUIKit.ProjectColorPalette;

    return colors.project[_customColor] ?? tokenColors[customColor] ?? customColor;
};

export const getBadgeInverseColors = ({
    colors
}: BadgeInverseColorsConstantType): BadgeInverseColors => {
    return {
        containerColor: colors.content.container.inverse,
        titleColor: colors.system.onInverse.neutral,
        iconColor: colors.system.onInverse.neutral,
        iconColorKey: "contrastHigh"
    };
};

export const getBadgeColors = ({
    isHovered = false,
    customTitleColor,
    customIconColor,
    customColor,
    isDisabled,
    variant,
    colors,
    type
}: BadgeColorsConstantType): BadgeColors => {
    const currentColors: BadgeColors = {
        ...getTypeColors({
            variant: getBadgeColorVariant({
                isDisabled,
                variant
            }),
            isDisabled,
            colors,
            type
        }),
        iconColorKey: getBadgeIconColorKey({
            isDisabled,
            variant,
            type
        })
    };

    if(isDisabled) {
        return currentColors;
    }

    if(variant === "solid" && type === "neutral") {
        Object.assign(currentColors, getBadgeInverseColors({
            colors
        }));
    }

    if(isHovered && variant === "ghost") {
        currentColors.containerColor = getColorRoleValue({
            role: TYPE_COLOR_ROLES[type].tonal.containerColor,
            colors
        });
    }

    if(customColor) {
        currentColors.containerColor = getBadgeCustomColor({
            tokenColors: colors.content.container,
            customColor,
            colors
        });
    }

    if(customTitleColor) {
        currentColors.titleColor = getBadgeCustomColor({
            tokenColors: colors.content.text,
            customColor: customTitleColor,
            colors
        });
    }

    if(customIconColor) {
        const _customIconColor = customIconColor as keyof NCoreUIKit.IconContentColors;

        currentColors.iconColor = getBadgeCustomColor({
            tokenColors: colors.content.icon,
            customColor: customIconColor,
            colors
        });

        if(colors.content.icon[_customIconColor]) {
            currentColors.iconColorKey = _customIconColor;
        }
    }

    return currentColors;
};

const stylesheet = StyleSheet.create({
    container: {
        justifyContent: "center",
        borderStyle: "solid",
        flexDirection: "row",
        alignItems: "center",
        maxWidth: "100%",
        flexShrink: 1
    },
    title: {
        textAlign: "center",
        maxWidth: "100%",
        flexShrink: 1,
        minWidth: 0
    },
    icon: {
        flexShrink: 0
    }
});

export const useStyles = ({
    spreadBehaviour,
    isFocusVisible,
    currentColors,
    isInteractive,
    isUnderlined,
    shapeConfigs,
    currentSize,
    isIconOnly,
    userStyle,
    radiuses,
    borders,
    colors,
    spaces
}: BadgeDynamicStyleType) => {
    const styles = {
        container: {
            paddingHorizontal: currentSize.paddingHorizontal,
            backgroundColor: currentColors.containerColor,
            borderColor: currentColors.borderColor,
            minHeight: currentSize.minHeight,
            minWidth: currentSize.minHeight,
            ...getCornerStyle({
                radius: radiuses.chip,
                shapeConfigs
            }),
            borderWidth: borders.line,
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        title: {
        } as Mutable<TextStyle>
    };

    if(isIconOnly) {
        delete styles.container.paddingHorizontal;
    }

    if(spreadBehaviour === "baseline") {
        Object.assign(styles.container, getHugStyle({
            style: userStyle
        }));
    }

    if(isInteractive) {
        Object.assign(styles.container, webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        }), getFocusRingStyle({
            colorKey: "default",
            isFocusVisible,
            borders,
            colors
        }));
    }

    if(isUnderlined) {
        styles.title.textDecorationLine = "underline";
    }

    return styles;
};
export default stylesheet;
